import React, { memo, Suspense, useState } from 'react';
import { Button, Dropdown, message, Tooltip } from 'antd';
import { useMixpanel } from 'react-mixpanel-browser';
import { useNavigate } from 'react-router-dom';
import { ApprovalWorkflowItemDto } from '@api/Api';

import { ReactComponent as CopySvg } from '@assets/icons/copy-secondary.svg';
import { ReactComponent as DeleteSvg } from '@assets/icons/interviews-delete-user.svg';
import { ReactComponent as SuccessSvg } from '@assets/icons/success.svg';
import { ReactComponent as ActionSvg } from '@assets/icons/action.svg';
import { useAuth } from '@hooks/useAuth';
import { MixpanelEventType, MixpanelService } from '@services/mixpanelService';
import { useDeleteApprovalWorkflow } from '@hooks/queries/approval';

import './CampaignApprovalActionMenu.less';

const DeleteApprovalModal = React.lazy(
  () => import('@components/Modals/ApprovalModal/DeleteApprovalModal')
);

type CampaignApprovalActionMenuProps = {
  approvalWorkflow: ApprovalWorkflowItemDto;
};

export default memo(function CampaignApprovalActionMenu({
  approvalWorkflow
}: CampaignApprovalActionMenuProps) {
  const mixpanel = useMixpanel();
  const { user } = useAuth(true);
  const navigate = useNavigate();
  const [deleteConfirmation, setDeleteConfirmation] = useState(false);
  const canManage = !!approvalWorkflow?.permissions?.manage;

  const {
    mutateAsync: deleteApprovalWorkflow,
    isPending: isLoadingDeleteApprovalWorkflow
  } = useDeleteApprovalWorkflow({});

  const isLoading = isLoadingDeleteApprovalWorkflow;

  const sendMixpanelEvent = () => {
    MixpanelService.track(mixpanel, user.id, MixpanelEventType.MANAGE_ASSETS, {
      addToFavorite: false,
      deleteAsset: false,
      newAsset: false,
      externalLinkShare: false,
      approvalRouting: true,
      newCommentAsset: false,
      annotationLeft: false,
      taskFromComment: false
    });
  };

  const onDeleteAW = async () => {
    if (isLoading) return;
    if (!approvalWorkflow) return;
    if (!canManage) return;
    try {
      await deleteApprovalWorkflow({
        id: approvalWorkflow.id
      });
      sendMixpanelEvent();
      navigate(
        `/campaigns/single/${approvalWorkflow.campaignId}?tab=approvals`
      );
      message.success({
        key: 'aw-deleted',
        content: `${approvalWorkflow.name} approval routing has been deleted.`,
        className: 'message-dark-modal',
        icon: <SuccessSvg className="anticon" />,
        duration: 2
      });
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Something went wrong');
    }
  };

  const onCopyLink = () => {
    if (!approvalWorkflow) return;
    navigator.clipboard.writeText(approvalWorkflow.url);
    message.success({
      content: 'Copied link to clipboard',
      className: 'message-dark-modal',
      icon: <SuccessSvg className="anticon" />
    });
  };

  return (
    <div className="campaign-approval-action-menu">
      <Dropdown
        trigger={['hover']}
        menu={{
          className: 'campaign-approval-action-menu-overlay',
          multiple: false,
          items: [
            {
              key: 'copyLink',
              label: (
                <Tooltip
                  overlayClassName="toggle_favorite_overlay"
                  title="Only reviewers in this approval can access this link"
                  placement="right"
                >
                  <div>Copy approval link</div>
                </Tooltip>
              ),
              icon: <CopySvg width={24} height={24} strokeWidth="1.5" />,
              onClick: onCopyLink
            },
            ...(canManage
              ? [
                  {
                    key: 'delete',
                    label: 'Delete entire routing',
                    icon: <DeleteSvg />,
                    onClick: () => setDeleteConfirmation(true)
                  }
                ]
              : [])
          ],
          onClick: ({ domEvent }) => {
            domEvent.stopPropagation();
          }
        }}
      >
        <Button
          type="text"
          disabled={isLoading}
          style={{ width: 24, height: 24, padding: 0 }}
        >
          <ActionSvg style={{ marginLeft: 0, marginRight: 0 }} />
        </Button>
      </Dropdown>
      <Suspense fallback={null}>
        <DeleteApprovalModal
          approvalWorkflowName={approvalWorkflow.name || ''}
          visible={deleteConfirmation}
          onCancel={() => setDeleteConfirmation(false)}
          onConfirm={onDeleteAW}
        />
      </Suspense>
    </div>
  );
});
